import { useMemo, useState } from "react";
import { documentService } from "@miplatform/api";
import { DOCUMENT_TYPES, HTTP_STATUSES } from "@miplatform/constants";
import { IDocument } from "@miplatform/types";
import { useComponentState } from "../useComponentState/useComponentState";

export const PROFILE_PICTURE_FILE_NAME = "profile-picture";

export const useProfilePicture = ({
  values,
  getExternalReferenceId,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  values: any;
  getExternalReferenceId: () => string;
}) => {
  const stateManager = useComponentState();
  const profilePicture = useMemo(
    () =>
      values?.documents?.find(
        (d: IDocument) => d.metadata_Title === PROFILE_PICTURE_FILE_NAME
      ) || values?.photo,
    [values]
  );

  const [pendingProfilePicture, setPendingProfilePicture] = useState(
    profilePicture ?? null
  );

  const uploadPicture = (file: File, externalReferenceId?: string) =>
    stateManager.run(async () => {
      const params = {
        documentType: DOCUMENT_TYPES.IMAGE,
        externalReferenceId: externalReferenceId ?? getExternalReferenceId(),
        title: PROFILE_PICTURE_FILE_NAME,
        applyWatermark: false,
      };
      const response = await documentService.documents.create({
        data: file,
        params,
        contentType: file.type,
        toastMessageConfig: {
          success: {
            text: "Profile picture uploaded successfully.",
          },
        },
      });

      if (response.status === HTTP_STATUSES.OK) {
        setPendingProfilePicture(null);
      }
      return response;
    });

  const removePicture = () =>
    stateManager.run(() =>
      documentService.documents.delete({
        id: profilePicture.id,
        modifiedOn: profilePicture.modifiedOn,
        toastMessageConfig: {
          failure: {
            text: "Profile picture update failed.",
          },
        },
      })
    );

  return {
    fileName: PROFILE_PICTURE_FILE_NAME,
    profilePicture,
    isLoading: stateManager.isBusy(),
    removePicture,
    uploadPicture,
    pendingProfilePicture,
    setPendingProfilePicture,
  };
};
